// Regeneration (§31 K5): once per round, +1 health and +1 plating, up to the
// piece's maximum, for a piece that sat still, went unhurt and is not under
// threat.
//
// **The stamps are per body** (`Position.lastMoved` / `lastHurt`, keyed by
// `pieceId`), so they travel with a piece through every door that moves one
// without any door being taught them. `stampBodies` writes them off a diff of
// the position before and after each command — every way a piece moves or is
// wounded is caught there, whatever the door — and `damage` stamps a hurt
// directly, for a plate chipped in the same command a regen put it back.
//
// Reversed the old armor rule ("nothing puts plating back"): plate now
// regenerates to `platingCap`, which `armNewborn`, `addPlating` and
// `reframePlating` keep.

import { type Direction, EAST, fileOf, NORTH, PAWN, pieceColor, pieceType, rankOf, SOUTH, WEST } from "./board";
import { ARTISAN } from "./rules";
import { isAttackedByEnemyOf } from "./chess/attacks";
import {
  hurtAt,
  pieceAt,
  pieceIdAt,
  platingAt,
  platingCapAt,
  type Position,
  setHurt,
  setPlating
} from "./position";

/**
 * A move's direction snapped to the nearest cardinal. A diagonal is a tie and
 * takes the smaller turn from `current`, else the vertical — a bishop off
 * the back rank keeps facing up the board.
 */
export function headingOf(from: number, to: number, current?: Direction): Direction {
  const dx = fileOf(to) - fileOf(from);
  const dy = rankOf(to) - rankOf(from);
  const vertical = dy > 0 ? NORTH : SOUTH;
  const horizontal = dx > 0 ? EAST : WEST;
  if (Math.abs(dy) > Math.abs(dx)) return vertical;
  if (Math.abs(dx) > Math.abs(dy)) return horizontal;
  if (current === undefined) return vertical;
  const turn = (d: Direction): number => Math.min((d - current + 4) % 4, (current - d + 4) % 4);
  return turn(horizontal) < turn(vertical) ? horizontal : vertical;
}

/**
 * Record who moved and who was hurt by one command, by body. `before` and
 * `after` are the positions either side of it; a no-op (one object) writes
 * nothing. Copy-on-write, so a map a reducer happened to share is never edited
 * under the state it came from.
 */
export function stampBodies(before: Position, after: Position): void {
  if (before === after || after.pieceId.size === 0) return;
  const was = new Map<number, number>();
  for (const [sq, id] of before.pieceId) was.set(id, sq);
  let moved: Map<number, number> | null = null;
  let hurt: Map<number, number> | null = null;
  let heading: Map<number, Direction> | null = null;
  for (const [sq, id] of after.pieceId) {
    const from = was.get(id);
    if (from === undefined) continue;
    const wounded = hurtAt(after, sq) > hurtAt(before, from);
    if (from !== sq) {
      moved ??= new Map(after.lastMoved);
      moved.set(id, after.round);
      // Faces where it went — unless its facing is rules (a pawn's, a
      // Pattern link's), or it was shoved, which a blow always wounds.
      if (!wounded && pieceType(pieceAt(after, sq)) !== PAWN && !after.facing.has(sq)) {
        heading ??= new Map(after.heading);
        heading.set(id, headingOf(from, sq, heading.get(id)));
      }
    }
    if (wounded) {
      hurt ??= new Map(after.lastHurt);
      hurt.set(id, after.round);
    }
  }
  if (moved) after.lastMoved = moved;
  if (hurt) after.lastHurt = hurt;
  if (heading) after.heading = heading;
}

/** A body was wounded this round — `damage`'s half of the stamp. */
export function stampHurt(pos: Position, square: number): void {
  const id = pieceIdAt(pos, square);
  if (id <= 0) return;
  pos.lastHurt = new Map(pos.lastHurt);
  pos.lastHurt.set(id, pos.round);
}

/**
 * Close round `closing`: every eligible piece regains one health and one plate.
 *
 * Eligible: did not move and took no damage in `closing` ("this round") or
 * `closing - 1` ("last round"), and no enemy attacks its square now. Ascending
 * squares, so two clients agree. Prunes the stamps of bodies no longer on the
 * board, since ids are never reused and nothing else would.
 */
export function regenerate(pos: Position, closing: number): void {
  const recent = (stamps: Map<number, number>, id: number): boolean => {
    const at = stamps.get(id);
    return at !== undefined && at >= closing - 1;
  };
  const live = new Set<number>();
  const squares = [...pos.board.keys()].sort((a, b) => a - b);
  for (const sq of squares) {
    const id = pieceIdAt(pos, sq);
    if (id > 0) live.add(id);
    const hurt = hurtAt(pos, sq);
    const plate = platingAt(pos, sq);
    const cap = platingCapAt(pos, sq);
    if (hurt === 0 && plate >= cap) continue;
    if (id > 0 && (recent(pos.lastMoved, id) || recent(pos.lastHurt, id))) continue;
    if (isAttackedByEnemyOf(pos, sq, pieceColor(pieceAt(pos, sq)))) continue;
    // An Artisan Pillar's side heals `ARTISAN.regen` (§31 A6); plates still one.
    const heal = pos.artisans.length > 0 && pos.artisans.includes(pieceColor(pieceAt(pos, sq))) ? ARTISAN.regen : 1;
    if (hurt > 0) setHurt(pos, sq, Math.max(0, hurt - heal));
    if (plate < cap) setPlating(pos, sq, plate + 1);
  }
  const prune = <T>(stamps: Map<number, T>): Map<number, T> => {
    for (const id of stamps.keys()) if (!live.has(id)) return new Map([...stamps].filter(([k]) => live.has(k)));
    return stamps;
  };
  pos.lastMoved = prune(pos.lastMoved);
  pos.lastHurt = prune(pos.lastHurt);
  pos.platingCap = prune(pos.platingCap);
  pos.heading = prune(pos.heading);
}
